/**
 * Listening metadata offline cache.
 *
 * The Listening overlay (Dialogues/Core), the main-flow pod-lap scheduler and
 * the L1 scheduler all read their STRUCTURE (pod sentence rows, Core seed
 * list, bookends, fine-known clip map, LEGO catalogue) live from Supabase.
 * The deliberate offline download caches the AUDIO, but those metadata
 * queries still hit the network — so in airplane mode the overlay showed
 * "listening_pod_sentences: TypeError: Load failed" and Core spun on a dead
 * fetch despite every clip being in IndexedDB.
 *
 * This module persists that metadata to IndexedDB — one entry per course,
 * the same pattern as the script cache in useScriptCache — and serves it back
 * when the app is offline or a live query fails. It is also the single source
 * for the listening bundle's audio-id set — the ids are derived from the very
 * rows we persist, so metadata and audio can't drift apart.
 *
 * THE SNAPSHOT'S LIFE, in order (all of it in this file):
 *
 *  1. WRITE — fetchAndCacheListeningMeta. Every slot Listening Mode lists
 *     (servedPod.resolveListeningPods: the served pod, then the named extra
 *     slots), its rows, clip texts and timings, bookends, fine-knowns, seeds,
 *     catalogue, L1 fallback phrases, and the course's content/audio stamps.
 *     All-or-nothing. Called by the deliberate offline download, and by:
 *  2. FIRST WRITE ON BOOT — ensureListeningMetaSnapshot (job #379). The
 *     online boot writes the snapshot when the device has none, so the
 *     Dialogues list exists offline before any deliberate download. The same
 *     call HEALS an entry that does not list every slot — see
 *     snapshotListsEverySlot for the two reasons (jobs #379 and #424).
 *  3. STALENESS — refreshListeningMetaIfStale. Content or audio stamp moved:
 *     mark the entry stale (persisted), refetch in the background.
 *  4. READ — getCachedListeningMeta, by every offline/fallback lane
 *     (useListeningPods, usePodLapScheduler, useLayer1Scheduler, servedPod's
 *     offline lane, and the fetch-ahead's pod corpus in LearningPlayer).
 *  5. WITHDRAWAL — clearCachedListeningPodRows when a live read says the
 *     course has no pods.
 */

import { openDB, deleteDB, type IDBPDatabase } from 'idb'
import type { SupabaseClient } from '@supabase/supabase-js'
import { fetchSceneNames } from './podSceneNames'
import { resolveListeningPods, isListeningPodLookupDegraded, wasListeningSnapshotHealed, markListeningSnapshotHealed, FALLBACK_POD_SLUG as SERVED_POD_SLUG } from './servedPod'
import {
  type L1FallbackPhraseRow,
  computeSeedLastLegoIndex,
  selectFallbackWinnerRows,
} from './layer1CupFallback'
import {
  collectAudioIds,
  getRevisedAudioRefs,
  stampRowAudioRefs,
  applyAudioRef,
  bareAudioId,
} from '../providers/revisedAudioRefs'

const META_DB_NAME = 'ssi-listening-meta'
const META_STORE = 'meta'

let metaDbPromise: Promise<IDBPDatabase> | null = null
const metaDb = (): Promise<IDBPDatabase> => {
  if (!metaDbPromise) {
    const open = () => openDB(META_DB_NAME, 1, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(META_STORE)) db.createObjectStore(META_STORE)
      },
    })
    metaDbPromise = (async () => {
      let db = await open()
      // Self-heal a storeless DB (interrupted upgrade) — same guard as the
      // script cache.
      if (!db.objectStoreNames.contains(META_STORE)) {
        db.close()
        await deleteDB(META_DB_NAME)
        db = await open()
      }
      return db
    })().catch((err) => {
      // A transient open failure must not poison every later cache access.
      metaDbPromise = null
      throw err
    })
  }
  return metaDbPromise
}

/**
 * The entry key is the course code and NOTHING else.
 *
 * It used to carry a hand-maintained `META_VERSION` prefix ('v2', 'v3') that
 * an author had to remember to bump whenever a content repair landed, which
 * orphaned every older entry. That constant was forgotten twice — the v2 bump
 * (2026-07-22) called itself a placeholder "pending structural updated_at-based
 * invalidation", and it was forgotten again until v3 on 2026-08-24, by which
 * point devices had been serving the pre-repair Pod 1 split arrays for hours
 * (founder report: "the new Scene 15 is playing the old Scene 15 clips in the
 * app right now"). Twice wrong means the schema is wrong, not the operator.
 *
 * Freshness is now decided by the vintage stored INSIDE the entry —
 * `contentStamp` / `audioStamp`, compared against the live `courses` stamps the
 * app already fetches on every online boot (refreshListeningMetaIfStale). The
 * stamps are DB-trigger-maintained, so any learner-visible content write moves
 * them with no human in the loop. Verified live 2026-08-24: for ita/fra/spa,
 * `courses.content_stamp` equals `max(listening_pod_sentences.updated_at)` to
 * the microsecond, so a pod repair moves the stamp by itself.
 *
 * The key deliberately cannot BE the stamp: offline the app does not know the
 * live stamp, and an offline learner with a slightly-old snapshot is a working
 * app while an offline learner with no snapshot is a broken one.
 */
const idbKey = (courseCode: string): string => courseCode

/** Legacy hand-versioned keys ('v2:ita_for_eng', 'v3:ita_for_eng') still on
 *  devices in the wild — v3 shipped to production on 2026-08-24. Matched so
 *  they can be migrated onto the bare key rather than silently abandoned. */
const LEGACY_KEY = /^v\d+:(.+)$/

/** Raw listening_pod_sentences row — the UNION of the column sets the
 *  Listening overlay and the pod-lap scheduler select, so one cached copy
 *  serves both readers. */
export interface CachedPodRow {
  id: string
  scene_number: number
  sentence_number: number | null
  global_order: number
  speaker: string | null
  target_text: string
  known_text: string
  target_audio_id: string | null
  known_audio_id: string | null
  glue_to_next: boolean
  atom_map?: unknown[] | null
  sentence_audio_ids?: string[] | null
  sentence_known_audio_ids?: string[] | null
  atom_map_fine?: unknown[] | null
  window_known_map?: unknown[] | null
  takeg_audio_ids?: Array<string | null> | null
  /** Jump-in marker (job #471) — rides the snapshot so offline changeovers
   *  match online ones. Absent on snapshots written before the column. */
  jump_in?: boolean | null
}

/** course_seeds row — superset of the Core tab's and the L1 scheduler's
 *  column sets. */
export interface CachedCoreSeed {
  seed_number: number
  known_text: string
  target_text: string
  target_text_roman: string | null
  known_audio_id: string | null
  target1_audio_id: string | null
  target2_audio_id: string | null
}

export interface CachedBookend {
  role: string
  text: string
  id: string
  duration_ms?: number
}

export interface CachedListeningMeta {
  courseCode: string
  cachedAt: number
  /** courses.content_stamp at fetch time — the content vintage this entry was
   *  built from. Trigger-maintained in the DB (any learner-visible content
   *  write moves it), compared on online boot by refreshListeningMetaIfStale.
   *  Absent on entries written before the stamp existed → treated as stale
   *  once (which retroactively heals every pre-stamp device). */
  contentStamp?: string
  /** courses.audio_stamp at fetch time — the AUDIO vintage. A-86: a clip
   *  repair moves audio_stamp and NOT necessarily content_stamp, so the
   *  content lane alone would leave a downloaded snapshot pointing at the
   *  pre-repair ref forever. Absent → pre-stamp entry, refreshed once. */
  audioStamp?: string
  /** Set when a live stamp comparison found this entry out of date and the
   *  background refresh has not landed yet; cleared by a successful fetch.
   *
   *  It is PERSISTED on purpose. The refresh used to be in-memory and
   *  fire-and-forget, which meant it healed only on a boot where the network
   *  was good — and a device with a good network never needs the snapshot in
   *  the first place. The device that actually serves stale rows is the one on
   *  a stalled connection (`isOfflineish`), where the same stall kills the
   *  refresh, so nothing was ever remembered and nothing ever healed. Writing
   *  the mark down means the staleness survives the reload and the retry
   *  happens on every subsequent boot until it succeeds. */
  stale?: {
    since: number
    liveContentStamp?: string
    liveAudioStamp?: string
  }
  /** listening_pod_sentences rows for the pod this course SERVES (see
   *  servedPod), in global_order. Empty array = the course genuinely has no
   *  pod (a valid, downloaded state) — entry ABSENT means "never downloaded". */
  podRows: CachedPodRow[]
  /** The serving slug `podRows` was fetched from — `pod-1`. Read back by
   *  servedPod's offline lane, which is how a learner who downloaded Croatian
   *  resolves it offline with no round-trip. A snapshot written before the
   *  2026-09-13 rename carries the retired slug and is mapped forward on read
   *  by `forwardLegacyPodSlug`; absent on entries older than the field. */
  podSlug?: string
  /** `listening_pods.title` of the served pod — Listening Mode's group heading
   *  when the course lists more than one pod. Absent on older entries. */
  podTitle?: string
  /** Popty's scene names for the served pod, in the course's known language
   *  (`listening_pods.metadata.scene_names`, Tom 2026-11-02) — so an offline
   *  learner still sees "Scene 8 - Name". Absent on older entries → "Scene N". */
  podSceneNames?: Record<string, string>
  /** EXTRA Listening Mode slots (servedPod rule 6, job #354): the named
   *  extra pods this course had when the snapshot was taken, each with its
   *  own rows, so OFFLINE Listening Mode lists them too. `podRows`/`podSlug`
   *  keep meaning the ONE served pod that main flow reads — this field is
   *  separate on purpose so servedPod's offline lane is unchanged. Absent on
   *  entries written before the third slot existed → no extras until the next
   *  refresh, which is exactly the pre-slot behaviour. */
  extraPods?: CachedExtraPod[]
  /** True when `extraPods` was written off a FALLBACK slot lookup (timeout,
   *  server error, offline) rather than a live read — so it may be missing
   *  slots the course really has. ensureListeningMetaSnapshot treats such an
   *  entry like a pre-slot one and refreshes it once on the next boot (job
   *  #424). Absent on a live-read entry and on every older entry. */
  extrasDegraded?: true
  /** The served slug a PRE-#544 snapshot recorded — a role-addressed topic
   *  pod (the Senedd pod for its holder) written into the served slot between
   *  2026-09-03 and 2026-09-13, when such a pod REPLACED pod-1 for its holder.
   *  Set on read by `forwardPreFixServedPod` once it has demoted that pod to
   *  its own card; read by snapshotListsEverySlot so the once-per-session heal
   *  rewrites the entry on the next online boot (job #553). Never set by a
   *  fresh fetch, so a healed entry carries it no longer. */
  preFixServedSlug?: string
  /** course_audio id → text for the split-clip ids referenced by podRows and
   *  by every extra pod's rows — the overlay's per-sentence display-text
   *  oracle (splitRowUnits). */
  clipTexts: Record<string, string>
  /** course_audio id → word timings (as stored) for every pod target clip
   *  that has them — the Immersion tracker's raw material (job #408). Rides
   *  with the clip in the same snapshot, no cache of its own. Absent on
   *  entries written before the tracker → no timings offline until the
   *  next refresh, which renders exactly as before. */
  clipTimings?: Record<string, unknown>
  /** bookend_listen_intro / bookend_listen_outro rows. */
  bookends: CachedBookend[]
  /** pod_fine_known clips: text_normalized → clip id (fusion-rung knowns). */
  fineKnowns: Record<string, string>
  /** Every seed in the course (Core tab + L1 scheduler). */
  coreSeeds: CachedCoreSeed[]
  /** course_legos (seed_number, lego_index) catalogue — L1 ordinals. */
  legoCatalogue: Array<{ seed_number: number; lego_index: number }>
  /** Last-LEGO basket phrase rows for seeds with NO target audio of their own
   *  — the L1 cup fallback's raw material (Tom's ruling, 2026-09-06; derived
   *  by useLayer1Scheduler's deriveSeedFallbackAudio). Absent on entries
   *  written before the fallback existed — those seeds just skip offline,
   *  which is the pre-fallback behaviour, until the next refresh. */
  l1FallbackPhrases?: CachedL1FallbackPhrase[]
}

/** One extra Listening Mode pod in the snapshot. */
export interface CachedExtraPod {
  slug: string
  title: string | null
  podRows: CachedPodRow[]
  /** Scene names for this pod, as podSceneNames. Absent on older entries. */
  sceneNames?: Record<string, string>
  /** True for a role-addressed topic pod (servedPod rule 5): recorded so the
   *  offline read-back gate can tell it from a parked slug. */
  addressed?: true
}

/** Mirrors useLayer1Scheduler's L1FallbackPhraseRow (kept structural, not
 *  imported, so the cache module stays dependency-light). */
export interface CachedL1FallbackPhrase {
  seed_number: number
  lego_index: number
  phrase_role: string | null
  known_text: string
  target_text: string
  known_audio_id: string | null
  target1_audio_id: string | null
  target2_audio_id: string | null
  target1_duration_ms: number | null
}

/**
 * Adopt a legacy hand-versioned entry ('v3:<course>') onto the bare key.
 *
 * Devices in the wild hold v3 entries right now (shipped to production
 * 2026-08-24) and older v2 ones. Dropping them would leave a learner who is
 * offline at the moment of upgrade with NO snapshot and no way to re-download
 * — a regression. So the entry MOVES instead, carrying its own `contentStamp`
 * with it, and the stamp comparison then decides whether it is fresh. That is
 * the whole point of keying on vintage rather than on a version prefix: a
 * migrated entry needs no special handling, it just gets judged.
 *
 * Newest-by-`cachedAt` wins if several keys exist. Every legacy key is deleted
 * either way, so this runs once per device and then costs nothing.
 */
const migrateLegacyEntry = async (
  db: IDBPDatabase,
  courseCode: string,
): Promise<CachedListeningMeta | null> => {
  const keys = (await db.getAllKeys(META_STORE)) as IDBValidKey[]
  const legacy = keys.filter(
    (k) => typeof k === 'string' && LEGACY_KEY.exec(k)?.[1] === courseCode,
  ) as string[]
  if (legacy.length === 0) return null

  let best: CachedListeningMeta | null = null
  for (const key of legacy) {
    const entry = (await db.get(META_STORE, key)) as CachedListeningMeta | undefined
    if (entry && (!best || (entry.cachedAt ?? 0) > (best.cachedAt ?? 0))) best = entry
    await db.delete(META_STORE, key)
  }
  if (!best) return null

  // `courseCode` on a hand-written test fixture can disagree with its key —
  // the bare key is authoritative from here on.
  const adopted: CachedListeningMeta = { ...best, courseCode }
  await db.put(META_STORE, JSON.parse(JSON.stringify(adopted)), idbKey(courseCode))
  console.log('[ListeningMeta] migrated legacy versioned entry for', courseCode,
    `(${legacy.join(', ')} → bare key, vintage ${adopted.contentStamp ?? 'pre-stamp'})`)
  return adopted
}

export const getCachedListeningMeta = async (
  courseCode: string,
): Promise<CachedListeningMeta | null> => {
  try {
    const db = await metaDb()
    const data = (await db.get(META_STORE, idbKey(courseCode))) as CachedListeningMeta | undefined
    if (data) {
      // A bare-key entry wins outright, but any legacy key left beside it is
      // dead weight — clear it so the scan stays cheap.
      void migrateLegacyEntryCleanup(db, courseCode)
      return forwardPreFixServedPod(forwardLegacyPodSlug(data))
    }
    return forwardPreFixServedPod(forwardLegacyPodSlug(await migrateLegacyEntry(db, courseCode)))
  } catch (err) {
    console.warn('[ListeningMeta] read failed:', (err as any)?.message, err)
    return null
  }
}

/**
 * The slug core pods carried before Tom's 2026-09-13 ruling retired it
 * ("there is only pod-1 now"). Production renamed every pod, sentence id and
 * progress row that day; the ONE place the old name may still turn up is a
 * download snapshot written to a learner's device before the rename. This is
 * the only reference to it in the app outside servedPod's migration note.
 */
const RETIRED_POD_SLUG = 'pod-0'
const RETIRED_ID_SEGMENT = `:${RETIRED_POD_SLUG}:`

/**
 * Map a pre-rename snapshot forward so servedPod's offline lane (rule 1) does
 * not drop it as a parked slug: the served slug becomes the one the server now
 * serves, and every pod row id moves to the renamed segment so progress the
 * scheduler files from this snapshot lands on the same ids the server
 * migrated to. Pure; a snapshot on any other slug is returned untouched. Can
 * be deleted once no device could still hold a snapshot from before
 * 2026-09-13 — the content-stamp lane rewrites the entry on the next online
 * boot, so that is a matter of weeks, not of releases.
 */
export const forwardLegacyPodSlug = <T extends CachedListeningMeta | null>(entry: T): T => {
  if (!entry || entry.podSlug !== RETIRED_POD_SLUG) return entry
  const renamed = (id: unknown) =>
    typeof id === 'string' ? id.replace(RETIRED_ID_SEGMENT, `:${SERVED_POD_SLUG}:`) : id
  return {
    ...entry,
    podSlug: SERVED_POD_SLUG,
    podRows: (entry.podRows || []).map((r) => ({ ...r, id: renamed(r.id) as string })),
  } as T
}

/**
 * Map a PRE-#544 snapshot forward so a role-addressed topic pod never plays
 * as "Pod 1" offline (Tom's decision, 2026-09-13, job #544: a topic pod is
 * its own card AFTER pod-1, never in place of it; job #553 for the snapshot).
 *
 * Between 2026-09-03 (fb3a4c727: serve a pod that names a role to the person
 * it names) and 2026-09-13 (18ef7424e) the resolver put the addressed pod in
 * the SERVED slot for its holder, so a snapshot written then for such a
 * holder carries the Senedd pod as `podSlug`/`podRows` — and no pod-1 at
 * all, because the slot lookup never asked for one. Nothing rewrote it: the
 * heal gate saw `extraPods: []` from a live read and was satisfied, and the
 * content stamp had not moved. Offline, useListeningPods listed those rows in
 * the first slot, labelled by the slot ("Pod 1") whenever the entry predated
 * the title field (2026-09-12), and main flow's pod lap played them.
 *
 * On read, the same rule as online:
 *  - the served slot holds pod-1 or nothing: a real pod-1 found among the
 *    extras (never written in practice, but the cheap case to honour) moves
 *    up; otherwise the slot is empty, exactly what a learner with no pod-1
 *    downloaded has, so main flow (rule 1) never plays the topic pod;
 *  - the recorded pod becomes an addressed extra AFTER the named ones, under
 *    its own row title — or, when the entry predates the title field, a
 *    reading of its slug, because the one label it must never wear is the
 *    slot's. Its rows and every clip they name stay: nothing downloaded goes
 *    dark ("play what you have", Tom 2026-08-15);
 *  - `preFixServedSlug` records what happened so the heal refetches once.
 * Pure; an entry served from pod-1 (or with no served pod) is returned as is.
 */
export const forwardPreFixServedPod = <T extends CachedListeningMeta | null>(entry: T): T => {
  if (!entry || typeof entry.podSlug !== 'string' || entry.podSlug === SERVED_POD_SLUG) return entry
  const oldSlug = entry.podSlug
  const extras = Array.isArray(entry.extraPods) ? entry.extraPods : []
  const realPod1 = extras.find((e) => e?.slug === SERVED_POD_SLUG)
  const demoted: CachedExtraPod = {
    slug: oldSlug,
    title: typeof entry.podTitle === 'string' && entry.podTitle !== '' ? entry.podTitle : titleFromSlug(oldSlug),
    podRows: entry.podRows || [],
    addressed: true,
  }
  const rest = extras.filter((e) => e?.slug !== SERVED_POD_SLUG && e?.slug !== oldSlug)
  return {
    ...entry,
    podSlug: realPod1 ? SERVED_POD_SLUG : undefined,
    podTitle: typeof realPod1?.title === 'string' ? realPod1.title : undefined,
    podRows: realPod1 ? realPod1.podRows || [] : [],
    extraPods: [...rest, demoted],
    preFixServedSlug: oldSlug,
  } as T
}

/** 'senedd-s4c-steve' → 'Senedd s4c steve': the last-resort card label for a
 *  demoted topic pod whose snapshot predates the title field. Not a name we
 *  would choose, but never the slot's. */
const titleFromSlug = (slug: string): string => {
  const words = slug.split(/[-_]+/).filter(Boolean).join(' ')
  return words.charAt(0).toUpperCase() + words.slice(1)
}

/** Delete legacy keys once a bare-key entry already exists. Never throws. */
const migrateLegacyEntryCleanup = async (
  db: IDBPDatabase,
  courseCode: string,
): Promise<void> => {
  try {
    const keys = (await db.getAllKeys(META_STORE)) as IDBValidKey[]
    for (const k of keys) {
      if (typeof k === 'string' && LEGACY_KEY.exec(k)?.[1] === courseCode) {
        await db.delete(META_STORE, k)
      }
    }
  } catch { /* cache hygiene must never break playback */ }
}

const setCachedListeningMeta = async (meta: CachedListeningMeta): Promise<void> => {
  try {
    // Round-trip to plain data — IndexedDB structured-clone rejects Vue
    // proxies (same guard as setCachedScript).
    const plain = JSON.parse(JSON.stringify(meta)) as CachedListeningMeta
    const db = await metaDb()
    await db.put(META_STORE, plain, idbKey(meta.courseCode))
    console.log('[ListeningMeta] saved to IndexedDB:', meta.courseCode,
      `(${meta.podRows.length} pod rows, ${meta.coreSeeds.length} seeds)`)
  } catch (err) {
    console.warn('[ListeningMeta] write failed:', (err as any)?.message, err)
  }
}

/**
 * Drop the cached pod rows for a course, keeping the rest of its entry
 * (coreSeeds, legoCatalogue, bookends) intact.
 *
 * Called when a LIVE read reports the course has no pod sentences at all.
 * That is how unreleased Layer 2 content is held back: every learner-facing
 * pod path queries the exact id of the pod the course SERVES, and servedPod
 * will only ever serve `pod-1` — so a pod parked on any other slug
 * (`unrecorded`, `gated-2026-08-06`) reads as "no pods yet" (the Welsh pods
 * were gated this way on 2026-08-06 — Aran and Catrin have not recorded
 * them). Without this, a
 * learner who downloaded the pod for offline use would keep replaying the
 * withdrawn snapshot forever, because the offline lane never re-checks.
 *
 * Deliberately keyed on "server says zero", not on a course allow-list: it
 * needs no maintenance, and it reverses itself the moment the recordings land
 * and the pod returns to a serving slug.
 */
export const clearCachedListeningPodRows = async (courseCode: string): Promise<void> => {
  try {
    const existing = await getCachedListeningMeta(courseCode)
    if (!existing || (existing.podRows.length === 0 && !(existing.extraPods?.length))) return
    // Drop podSlug with the rows: the snapshot no longer describes any pod,
    // so it must not keep asserting one to servedPod's offline lane. The
    // extra slots go with it — they are listed under the served pod.
    await setCachedListeningMeta({
      ...existing, podRows: [], clipTexts: {}, podSlug: undefined, podTitle: undefined, extraPods: [],
    })
    console.log('[ListeningMeta] dropped', existing.podRows.length,
      'stale offline pod rows for', courseCode, '— course reports no pods live')
  } catch (err) {
    // Never let cache hygiene break playback.
    console.warn('[ListeningMeta] pod-row clear failed:', (err as any)?.message, err)
  }
}

/** Column list for the pod-row union select — keep in sync with CachedPodRow. */
export const POD_ROW_COLUMNS =
  'id, scene_number, sentence_number, global_order, speaker, target_text, known_text, ' +
  'target_audio_id, known_audio_id, glue_to_next, atom_map, ' +
  'sentence_audio_ids, sentence_known_audio_ids, atom_map_fine, window_known_map, takeg_audio_ids, ' +
  // Carried so the OFFLINE lane can draw the same walk/continuation line the
  // online one does. A snapshot without these columns would hand every reader
  // rows it could not classify, and a continuation would read as a line of the
  // walk — a learner's walk lengthening on a plane, silently.
  'variant_key, attach_sentence_number, ' +
  // The jump-in changeover marker (job #471) — offline dialogues must
  // breathe the same way online ones do.
  'jump_in'

/** Column list for the pod CLIP read (split-clip texts + word timings) —
 *  shared with useListeningPods so online and offline read the same shape.
 *  `word_boundaries` is the Azure shape; `word_timings` is the #407 contract
 *  column, live on course_audio since 2026-09-12 and written by Cartesia-minted
 *  pod clips. There was no backfill, so every older clip carries NULL there
 *  and falls through to `word_boundaries` in readClipTimings. */
export const POD_CLIP_COLUMNS = 'id, text, word_boundaries, word_timings'

/** The timing payload of one course_audio row, whichever column carries it.
 *  The #407 contract column wins when present; the Azure boundaries are the
 *  fallback. Null when neither is set. Normalised at render time by
 *  playback/breathGroups.ts — stored as-is so the snapshot stays raw. */
export const readClipTimings = (clip: { word_timings?: unknown; word_boundaries?: unknown } | null | undefined): unknown | null => {
  if (!clip) return null
  const raw = clip.word_timings ?? clip.word_boundaries ?? null
  if (!raw || typeof raw !== 'object') return null
  if (Array.isArray(raw) && raw.length === 0) return null
  return raw
}

const PAGE = 1000

/** The one backoff every listening-metadata retry uses: 500 ms, then 1000 ms. */
const listeningRetryDelay = (attempt: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, 500 * attempt))

/**
 * Fetch every piece of listening metadata for the course and persist it as
 * one cache entry. Called from the deliberate offline download. Returns the
 * fresh meta, or null on any fetch failure (nothing partially written — the
 * entry is all-or-nothing so "entry present" always means "fully usable").
 *
 * Retries a few times on transient failure before giving up — this call
 * chains 6+ network round-trips, run right as the user is about to go
 * offline (the worst possible moment for a transient blip), and previously
 * had zero retry: one flaky request silently dropped the entire Core/
 * Listening bundle from the offline download while it still reported
 * "complete" (2026-07-21 flight report).
 */
export const fetchAndCacheListeningMeta = async (
  client: SupabaseClient,
  courseCode: string,
  attempts = 3,
): Promise<CachedListeningMeta | null> =>
  retryListeningRead(
    () => fetchAndCacheListeningMetaOnce(client, courseCode),
    (meta) => meta !== null,
    attempts,
  )

/**
 * Retry a live listening-metadata read before falling back to the (possibly
 * stale, unbounded-age, course-only-keyed) offline snapshot. Shared by every
 * runtime reader (podLapScheduler, layer1Scheduler, useListeningPods) — they
 * previously fell back on the FIRST failure with zero retry, and the highest-
 * risk moment for a transient failure is right after a forced sign-in reload
 * (auth/network still settling), which is exactly when the stale-snapshot
 * fallback is most likely to silently serve wrong-vintage audio/text
 * (2026-07-21 forum report). fetchAndCacheListeningMeta is built on it too.
 */
export const retryListeningRead = async <T>(
  fn: () => Promise<T>,
  isOk: (result: T) => boolean,
  attempts = 3,
): Promise<T> => {
  let result = await fn()
  for (let attempt = 1; attempt < attempts && !isOk(result); attempt++) {
    await listeningRetryDelay(attempt)
    result = await fn()
  }
  return result
}

/** Retry variant for readers that signal failure by THROWING (useListeningPods'
 *  loadFromNetwork) rather than returning a Supabase {error} result. */
export const retryListeningReadOrThrow = async <T>(
  fn: () => Promise<T>,
  attempts = 3,
): Promise<T> => {
  for (let attempt = 1; attempt < attempts; attempt++) {
    try {
      return await fn()
    } catch {
      await listeningRetryDelay(attempt)
    }
  }
  return fn()
}

const fetchAndCacheListeningMetaOnce = async (
  client: SupabaseClient,
  courseCode: string,
): Promise<CachedListeningMeta | null> => {
  try {
    // Served pod first (rule 1), then the named extra Listening Mode slots
    // the course has (rule 6). One sentence read per pod.
    const listeningPods = await resolveListeningPods(client, courseCode)
    const extrasDegraded = isListeningPodLookupDegraded(courseCode)
    const [servedEntry, ...extraEntries] = listeningPods
    const { podId, slug: podSlug, title: podTitle } = servedEntry
    const readRows = (id: string) =>
      client
        .from('listening_pod_sentences')
        .select(POD_ROW_COLUMNS)
        .eq('pod_id', id)
        .order('global_order', { ascending: true })
    // Never rejects (a failed read costs the names, not the snapshot).
    const sceneNamesRead = fetchSceneNames(client, [podId, ...extraEntries.map((e) => e.podId)])
    const [podsResult, bookendsResult, stampResult, ...extraResults] = await Promise.all([
      readRows(podId),
      client
        .from('course_audio')
        .select('role, text, id, duration_ms')
        .eq('course_code', courseCode)
        .in('role', ['bookend_listen_intro', 'bookend_listen_outro']),
      client
        .from('courses')
        .select('content_stamp, audio_stamp')
        .eq('course_code', courseCode)
        .maybeSingle(),
      ...extraEntries.map((e) => readRows(e.podId)),
    ])
    if (podsResult.error) throw new Error(`listening_pod_sentences: ${podsResult.error.message}`)
    for (const r of extraResults) {
      if (r.error) throw new Error(`listening_pod_sentences (extra slot): ${r.error.message}`)
    }
    if (bookendsResult.error) throw new Error(`bookends: ${bookendsResult.error.message}`)
    // A-86: this snapshot is the OFFLINE source of truth for the listening
    // lane, so it must be written with per-clip versioned refs (`<uuid>.v<N>`)
    // already applied. The schedulers stamp what they fetch, but offline the
    // revised-ref lookup cannot run and would fall back to an empty map — so
    // an unstamped snapshot means an offline learner plays the pre-repair clip
    // for as long as the snapshot lives. Stamping here, while online, is the
    // only place that can be fixed.
    // Looked up for exactly the ids on the rows being stamped (just in time,
    // Tom 2026-09-23) — the shared map tops itself up page by page below.
    let revisedRefs = await getRevisedAudioRefs(
      client,
      courseCode,
      collectAudioIds(podsResult.data || [], bookendsResult.data || [], ...extraResults.map((r) => r.data || [])),
    )
    const podRows = stampRowAudioRefs(
      revisedRefs,
      (podsResult.data || []) as unknown as CachedPodRow[],
    )
    const sceneNamesByPod = await sceneNamesRead
    const extraPods: CachedExtraPod[] = extraEntries.map((e, i) => ({
      slug: e.slug,
      title: e.title,
      ...(sceneNamesByPod.get(e.podId) ? { sceneNames: sceneNamesByPod.get(e.podId) } : {}),
      ...(e.addressed ? { addressed: true as const } : {}),
      podRows: stampRowAudioRefs(
        revisedRefs,
        (extraResults[i]?.data || []) as unknown as CachedPodRow[],
      ),
    }))

    // Split-clip display texts (the overlay's per-sentence oracle) — chunked
    // to keep the PostgREST in() URL short, mirroring useListeningPods.
    const clipIds = new Set<string>()
    // Whole-turn target clips ride the same read for their word timings (the
    // Immersion tracker, job #408); they never enter clipTexts' oracle.
    const turnClipIds = new Set<string>()
    for (const row of [...podRows, ...extraPods.flatMap((e) => e.podRows)]) {
      for (const id of row.sentence_audio_ids || []) if (id) clipIds.add(id)
      for (const id of row.sentence_known_audio_ids || []) if (id) clipIds.add(id)
      if (row.target_audio_id) turnClipIds.add(row.target_audio_id)
      for (const id of row.takeg_audio_ids || []) if (id) turnClipIds.add(id)
    }
    // clipIds now carry `.vN`, but course_audio is keyed by the BARE uuid — so
    // query bare and key the result by the stamped ref the overlay will look up.
    const clipTexts: Record<string, string> = {}
    const clipTimings: Record<string, unknown> = {}
    const stampedByBare = new Map(Array.from(clipIds).map((ref) => [bareAudioId(ref), ref]))
    const turnStampedByBare = new Map(Array.from(turnClipIds).map((ref) => [bareAudioId(ref), ref]))
    const bareArr = Array.from(new Set([...stampedByBare.keys(), ...turnStampedByBare.keys()]))
    for (let i = 0; i < bareArr.length; i += 150) {
      const { data: clips, error: clipErr } = await client
        .from('course_audio')
        .select(POD_CLIP_COLUMNS)
        .in('id', bareArr.slice(i, i + 150))
      if (clipErr) throw new Error(`split-clip texts: ${clipErr.message}`)
      for (const c of clips || []) {
        const splitRef = stampedByBare.get(c.id)
        if (splitRef) clipTexts[splitRef] = c.text || ''
        const timings = readClipTimings(c)
        if (timings) {
          for (const ref of [splitRef, turnStampedByBare.get(c.id)]) if (ref) clipTimings[ref] = timings
        }
      }
    }

    // Fine-known clips (fusion-rung glosses) — paged under PostgREST's cap.
    const fineKnowns: Record<string, string> = {}
    for (let from = 0; ; from += PAGE) {
      const { data, error } = await client
        .from('course_audio')
        .select('id, text_normalized')
        .eq('course_code', courseCode)
        .eq('role', 'pod_fine_known')
        .range(from, from + PAGE - 1)
      if (error) throw new Error(`fine-knowns: ${error.message}`)
      revisedRefs = await getRevisedAudioRefs(client, courseCode, collectAudioIds(data || []))
      for (const r of data || []) fineKnowns[r.text_normalized] = applyAudioRef(revisedRefs, r.id)!
      if (!data || data.length < PAGE) break
    }

    // Every seed, paginated — a single .limit() silently truncates on big
    // courses (banked lesson).
    const coreSeeds: CachedCoreSeed[] = []
    for (let from = 0; ; from += PAGE) {
      const { data, error } = await client
        .from('course_seeds')
        .select('seed_number, known_text, target_text, target_text_roman, known_audio_id, target1_audio_id, target2_audio_id')
        .eq('course_code', courseCode)
        .order('seed_number', { ascending: true })
        .range(from, from + PAGE - 1)
      if (error) throw new Error(`course_seeds: ${error.message}`)
      revisedRefs = await getRevisedAudioRefs(client, courseCode, collectAudioIds(data || []))
      for (const r of stampRowAudioRefs(revisedRefs, data || [])) coreSeeds.push(r as CachedCoreSeed)
      if (!data || data.length < PAGE) break
    }

    // LEGO catalogue for L1 ordinals — 10000 sits under the server max-rows
    // and comfortably above any real course (mirrors useLayer1Scheduler).
    const { data: catalogue, error: catErr } = await client
      .from('course_legos')
      .select('seed_number, lego_index')
      .eq('course_code', courseCode)
      .order('seed_number', { ascending: true })
      .order('lego_index', { ascending: true })
      .limit(10000)
    if (catErr) throw new Error(`course_legos: ${catErr.message}`)

    // L1 cup-fallback phrases (Tom's ruling, 2026-09-06): for seeds with NO
    // target audio of their own, persist the WINNING last-LEGO basket phrase —
    // one row per seed, selected by the same pure derivation the scheduler
    // runs live (selectFallbackWinnerRows), so the offline cup pours the same
    // clip and the offline download carries only those three ids per seed.
    // Empty on courses with complete seed audio.
    const needySeeds = coreSeeds
      .filter((s) => !s.target1_audio_id && !s.target2_audio_id)
      .map((s) => s.seed_number)
    let l1FallbackPhrases: CachedL1FallbackPhrase[] = []
    if (needySeeds.length > 0) {
      const phraseRows: L1FallbackPhraseRow[] = []
      for (let from = 0; ; from += PAGE) {
        const { data, error } = await client
          .from('course_practice_phrases')
          .select('seed_number, lego_index, phrase_role, known_text, target_text, known_audio_id, target1_audio_id, target2_audio_id, target1_duration_ms')
          .eq('course_code', courseCode)
          .in('seed_number', needySeeds)
          .not('known_audio_id', 'is', null)
          .not('target1_audio_id', 'is', null)
          .not('target2_audio_id', 'is', null)
          .range(from, from + PAGE - 1)
        if (error) throw new Error(`l1-fallback phrases: ${error.message}`)
        revisedRefs = await getRevisedAudioRefs(client, courseCode, collectAudioIds(data || []))
        for (const r of stampRowAudioRefs(revisedRefs, data || [])) {
          phraseRows.push(r as L1FallbackPhraseRow)
        }
        if (!data || data.length < PAGE) break
      }
      l1FallbackPhrases = selectFallbackWinnerRows(
        phraseRows,
        computeSeedLastLegoIndex((catalogue || []) as Array<{ seed_number: number; lego_index: number }>),
      )
    }

    // Stamp is best-effort: a failed stamp read must not drop the whole
    // bundle — the entry just lands stamp-less and refreshes next online boot.
    const stampRow = stampResult.data as
      | { content_stamp?: string; audio_stamp?: string }
      | null
    const contentStamp = stampRow?.content_stamp ?? undefined
    const audioStamp = stampRow?.audio_stamp ?? undefined

    const meta: CachedListeningMeta = {
      courseCode,
      cachedAt: Date.now(),
      contentStamp,
      audioStamp,
      podRows,
      podSlug,
      podTitle: podTitle ?? undefined,
      ...(sceneNamesByPod.get(podId) ? { podSceneNames: sceneNamesByPod.get(podId) } : {}),
      extraPods,
      ...(extrasDegraded ? { extrasDegraded: true as const } : {}),
      clipTexts,
      clipTimings,
      bookends: stampRowAudioRefs(revisedRefs, (bookendsResult.data || []) as CachedBookend[]),
      fineKnowns,
      coreSeeds,
      legoCatalogue: (catalogue || []) as Array<{ seed_number: number; lego_index: number }>,
      l1FallbackPhrases,
    }
    await setCachedListeningMeta(meta)
    return meta
  } catch (err) {
    console.warn('[ListeningMeta] fetch failed (nothing cached):', (err as any)?.message ?? err)
    return null
  }
}

/**
 * Structural freshness: if a cached listening bundle exists and its content
 * vintage differs from the course's live content_stamp, refetch the whole
 * bundle in the background. Called on online boot (checkContentVersion) with
 * the stamp it already fetched — no extra request. Never blocks play, never
 * touches anything when offline (the caller only has a stamp when the courses
 * query succeeded), and a failed refresh keeps the old entry (stale beats
 * empty) and retries next boot because the old stamp survives.
 *
 * Scope: refreshes METADATA (text, glosses, structure, audio-id references) —
 * the stale class that persisted for months. It does not re-run the audio
 * download: a re-recorded clip's new id streams (and caches) on first online
 * play, and wholesale audio regeneration still rides the content_version
 * full-clear lane in checkContentVersion.
 *
 * Returns true if a refresh was started.
 */
export const refreshListeningMetaIfStale = async (
  client: SupabaseClient,
  courseCode: string,
  liveStamp: string | null | undefined,
  liveAudioStamp?: string | null,
): Promise<boolean> => {
  if (!liveStamp && !liveAudioStamp) return false
  const cached = await getCachedListeningMeta(courseCode)
  if (!cached) return false // never downloaded — nothing to keep fresh

  const contentMoved = !!liveStamp && cached.contentStamp !== liveStamp
  const alreadyMarked = !!cached.stale
  // A-86: a clip repair moves audio_stamp and not necessarily content_stamp.
  // Without this arm, a downloaded snapshot keeps pointing at the pre-repair
  // ref forever — the offline half of the stale-clip bug. An entry with no
  // audioStamp predates this field, so it refreshes once and heals itself.
  const audioMoved = !!liveAudioStamp && cached.audioStamp !== liveAudioStamp
  if (!contentMoved && !audioMoved) {
    // Stamps agree but a previous mark never cleared — the refresh that set it
    // must have landed some other way (or the DB moved back). Clear it so the
    // fallback logging does not cry wolf forever.
    if (alreadyMarked) await setCachedListeningMeta({ ...cached, stale: undefined })
    return false
  }

  console.log('[ListeningMeta]',
    contentMoved
      ? `content stamp moved (${cached.contentStamp ?? 'pre-stamp'} → ${liveStamp})`
      : `audio stamp moved (${cached.audioStamp ?? 'pre-stamp'} → ${liveAudioStamp})`,
    '— refreshing bundle')

  // Write the mark FIRST, and await it. The refresh below is fire-and-forget
  // by design (it must never block play), so if the device's connection dies
  // mid-refresh the only record that this snapshot is out of date would
  // otherwise die with it — which is exactly what happened to the Pod 1
  // repair. Persisted, the retry happens on every boot until one succeeds, and
  // any reader that falls back to this snapshot can say out loud that it is
  // serving known-stale rows.
  await setCachedListeningMeta({
    ...cached,
    stale: {
      since: cached.stale?.since ?? Date.now(),
      liveContentStamp: liveStamp ?? undefined,
      liveAudioStamp: liveAudioStamp ?? undefined,
    },
  })
  void fetchAndCacheListeningMeta(client, courseCode)
  return true
}

/**
 * Write the listening snapshot for a course that has NONE yet — the automatic
 * path's half of "play what you have" (Tom, 2026-08-15; re-ruled 2026-09-12 on
 * job #379: "the list must come from cache offline").
 *
 * Until now the snapshot was written by the deliberate Offline Mode download
 * alone, so a learner whose clips arrived through the automatic download-ahead
 * opened Listening Mode in airplane mode and read "Dialogues aren't downloaded
 * yet" — with the pod's audio already on the device. Reproduced headless on
 * production 5ea385e, Chinese for English speakers, 2026-09-12: no Offline Mode
 * download → 0 scenes offline; the same device after the download → 22.
 *
 * This is METADATA only (pod rows, clip texts, seeds, catalogue — the same
 * reads the schedulers make on every online boot), never audio, so it stays
 * inside the 2026-09-01 ruling that the automatic path never loads a corpus
 * up front. Once per device per course: an existing entry, fresh or stale, is
 * left to refreshListeningMetaIfStale — except one written before the extra
 * pod slots existed, which is refreshed once so every slot lists offline.
 * Never throws, never blocks.
 */
export const ensureListeningMetaSnapshot = async (
  client: SupabaseClient,
  courseCode: string,
): Promise<boolean> => {
  try {
    // At most once per session per course: this runs on every round advance,
    // and a snapshot still flagged from a degraded lookup would otherwise be
    // refetched and rewritten on each one (job #425).
    // Check before reading IDB so healed rounds don't clone the full snapshot.
    if (wasListeningSnapshotHealed(courseCode)) return false
    const cached = await getCachedListeningMeta(courseCode)
    if (cached && snapshotListsEverySlot(cached)) {
      // A complete snapshot from an earlier session needs no heal either.
      // Remember the check so every round does not clone it from IndexedDB.
      markListeningSnapshotHealed(courseCode)
      return false
    }
    // Marked healed only on a SUCCESSFUL write: a learner with no snapshot
    // whose first fetch fails on a flaky link keeps retrying on later round
    // advances, as before #425, and the once-per-session guard holds after
    // the write that actually landed (job #430).
    const written = !!(await fetchAndCacheListeningMeta(client, courseCode))
    if (written) markListeningSnapshotHealed(courseCode)
    return written
  } catch {
    return false
  }
}

/**
 * Does this snapshot's Listening Mode list carry every slot the course has?
 * The once-per-boot heal in ensureListeningMetaSnapshot refreshes an entry
 * exactly when this is false; the content-stamp lane never would, because
 * none of these reasons moves the stamp. Three reasons, one gate:
 *
 *  - no `extraPods` field at all: written before the extra slots existed
 *    (job #354, 2026-09-12), so it lists the served pod alone offline for as
 *    long as the content stamp stands still — how the Italian method pod
 *    vanished from Tom's airplane-mode list the same day (job #379);
 *  - `extrasDegraded`: its slots came from a fallback lookup (timeout, error,
 *    offline), so `extraPods` may be `[]` only because the first fetch timed
 *    out (job #424). servedPod marks that at the lookup; the writer files it;
 *  - `preFixServedSlug`: a role-addressed topic pod was written into the
 *    served slot before job #544, so the entry has no pod-1 and the topic
 *    pod was playing as "Pod 1" offline. forwardPreFixServedPod has already
 *    demoted it on read; this refetch writes the entry the way the fixed
 *    resolver lists it (job #553). Three reasons, one gate.
 *
 * A false answer triggers the refetch at most once per session per course
 * (servedPod's healed mark, job #425), since the caller runs on every round
 * advance and a degraded flag stands until the memo is reset.
 */
export const snapshotListsEverySlot = (cached: CachedListeningMeta): boolean =>
  Array.isArray(cached.extraPods) && !cached.extrasDegraded && !cached.preFixServedSlug

/**
 * Is the snapshot for this course known to be out of date? True only when a
 * live stamp comparison already said so and the refresh has not landed —
 * never a guess. Readers use it to log a stale fallback rather than to refuse
 * one: a stale snapshot still beats a blank screen for an offline learner.
 */
export const isCachedListeningMetaStale = async (courseCode: string): Promise<boolean> => {
  try {
    return !!(await getCachedListeningMeta(courseCode))?.stale
  } catch {
    return false
  }
}

/** The served pod's rows followed by every extra slot's rows — the full set
 *  of pod sentences a snapshot can play offline. */
export const allCachedPodRows = (meta: CachedListeningMeta): CachedPodRow[] => [
  ...meta.podRows,
  ...(meta.extraPods || []).flatMap((e) => e.podRows || []),
]

/**
 * The POD slice of the listening metadata — every slot's turns (incl.
 * per-sentence split clips, known glosses, Take-G fusion
 * slices), the fine-known gloss clips those scenes use, and the listen
 * bookends that top and tail a pod lap.
 *
 * This is what both fetch paths put in front of the course (Tom's ruling,
 * 2026-09-12, job #379 — playback/offlineDownloadOrder.ts): a learner who
 * disconnects partway through should have COMPLETE dialogue pods rather than
 * a scattering of everything. Core seed audio is deliberately NOT here —
 * Core is the whole-course listening bundle, not a pod.
 */
export const collectListeningMetaPodAudioIds = (meta: CachedListeningMeta): string[] => {
  const ids = new Set<string>()
  const add = (id?: string | null) => { if (id) ids.add(id) }
  for (const row of allCachedPodRows(meta)) {
    add(row.target_audio_id); add(row.known_audio_id)
    // clipTexts is populated from course_audio, so it also proves that a
    // slice exists. The player falls back to the whole turn if either side
    // is missing: the downloader must follow that same all-or-nothing rule
    // rather than request deleted slices again on every round boundary.
    const splits = [...(row.sentence_audio_ids || []), ...(row.sentence_known_audio_ids || [])].filter(Boolean)
    if (splits.every(id => Object.prototype.hasOwnProperty.call(meta.clipTexts || {}, id))) {
      for (const id of splits) add(id)
    }
    for (const id of row.takeg_audio_ids || []) add(id)
  }
  for (const b of meta.bookends) add(b.id)
  for (const id of Object.values(meta.fineKnowns)) add(id)
  return [...ids]
}

/**
 * Every audio id the listening metadata references — the listening slice of
 * the offline audio bundle. Derived from the same rows we persist, so the
 * cached metadata can never point at un-downloaded audio: Core seed
 * sentences, pod turns (incl. per-sentence split clips, Take-G
 * fusion slices), fine-known gloss clips, and the listen bookends.
 */
export const collectListeningMetaAudioIds = (meta: CachedListeningMeta): string[] => {
  const ids = new Set<string>()
  const add = (id?: string | null) => { if (id) ids.add(id) }
  for (const s of meta.coreSeeds) {
    add(s.known_audio_id); add(s.target1_audio_id); add(s.target2_audio_id)
  }
  // L1 cup-fallback winners — the phrase clips a seed with no audio of its
  // own pours instead (Tom's ruling, 2026-09-06). One phrase per needy seed.
  for (const p of meta.l1FallbackPhrases || []) {
    add(p.known_audio_id); add(p.target1_audio_id); add(p.target2_audio_id)
  }
  // Then the pod slice, in its own order — the same ids the fetch-ahead
  // paths put first, so the two collectors cannot drift apart.
  for (const id of collectListeningMetaPodAudioIds(meta)) add(id)
  return [...ids]
}
